import { FC, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { PageHeader } from '@new/common/ui/PageHeader/PageHeader';
import { Loader } from '@new/common/ui/Loader/Loader';
import { SearchInput } from '@new/common/ui/SearchInput/SearchInput';
import { ButtonInputs, ButtonInputsOption } from '@new/common/ui/ButtonInputs/ButtonInputs';
import { Button } from '@new/common/ui/Button/Button';
import { SortableTable, SortableTableColumn } from '@new/common/ui/Table/SortableTable';
import {
  useGetAdminEvaluationsQuery,
  type AdminEvaluationSummary,
  type AdminEvaluationsFilter,
  type AdminEvaluationsQuery,
} from '@teachur/http-client';

import { Plus } from 'lucide-react';

import { EvaluationStatusBadge } from '@new/features/transcripts/shared/components/StatusBadge';
import type { EvaluationStatus } from '@new/features/transcripts/shared/types';
import { EvaluationReviewDrawer } from '../components/EvaluationReviewDrawer/EvaluationReviewDrawer';
import { AdminAddTranscriptModal } from '../components/AdminAddTranscriptModal/AdminAddTranscriptModal';

const PAGE_SIZE = 10;

const FILTER_OPTIONS: ButtonInputsOption<AdminEvaluationsFilter>[] = [
  { label: 'All', value: 'all' },
  { label: 'Needs review', value: 'needs_review' },
  { label: 'Completed', value: 'completed' },
  { label: 'Drafts', value: 'draft' },
];

export const TranscriptsReviewPage: FC = () => {
  const navigate = useNavigate();
  const { evaluation: evaluationParam } = useSearch({ strict: false }) as {
    evaluation?: string | number;
  };

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<AdminEvaluationsFilter>('all');
  const [page, setPage] = useState(1);
  const [isAddOpen, setIsAddOpen] = useState(false);

  const parsedId = Number(evaluationParam);
  const selectedId = Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;

  const openEvaluation = (id: number) =>
    navigate({ search: (prev: Record<string, unknown>) => ({ ...prev, evaluation: id }) });

  const closeEvaluation = () =>
    navigate({
      search: (prev: Record<string, unknown>) => {
        const { evaluation: _drop, ...rest } = prev;
        return rest;
      },
    });

  const resetToFirstPage = () => setPage(1);

  const query: AdminEvaluationsQuery = useMemo(
    () => ({
      filter,
      search: search || undefined,
      page,
      pageSize: PAGE_SIZE,
    }),
    [filter, search, page]
  );

  const { data, isLoading, isFetching } = useGetAdminEvaluationsQuery(query);

  const evaluations = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const selectedPlaceholder = selectedId ? evaluations.find((e) => e.id === selectedId) : undefined;

  const columns: SortableTableColumn<AdminEvaluationSummary>[] = [
    {
      key: 'studentName',
      header: 'Student',
      className: 'max-w-[220px] truncate',
      render: (_, row) => row.studentName ?? `User #${row.userId}`,
    },
    {
      key: 'studentEmail',
      header: 'Email',
      className: 'max-w-[220px] truncate',
      render: (_, row) => row.studentEmail ?? '—',
    },
    {
      key: 'transcriptCount',
      header: 'Transcripts',
      render: (_, row) => row.transcriptCount,
    },
    {
      key: 'unofficialTotal',
      header: 'Unofficial',
      render: (_, row) => row.unofficialTotal,
    },
    {
      key: 'officialTotal',
      header: 'Official',
      render: (_, row) => row.officialTotal,
    },
    {
      key: 'status',
      header: 'Status',
      render: (_, row) => <EvaluationStatusBadge status={row.status as EvaluationStatus} />,
    },
  ];

  return (
    <div className="min-h-screen">
      <div className="flex flex-col gap-8 px-4 py-8 sm:px-6 lg:px-8">
        <PageHeader
          title="Transcripts Review"
          description="Review student transcript evaluations and manage their transfer credits"
          actions={
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Plus className="size-4" />}
              onClick={() => setIsAddOpen(true)}
              text="Create"
            />
          }
        />

        <section className="overflow-hidden rounded-xl border border-[#e1e7ea] bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-[#e1e7ea] px-4 py-4 sm:px-6">
            <SearchInput
              value={search}
              onChange={(v) => {
                setSearch(v);
                resetToFirstPage();
              }}
              placeholder="Search by name, email, or former name"
            />
            <ButtonInputs<AdminEvaluationsFilter>
              value={filter}
              onChange={(v) => {
                setFilter(v);
                resetToFirstPage();
              }}
              options={FILTER_OPTIONS}
            />
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader />
            </div>
          ) : (
            <SortableTable<AdminEvaluationSummary>
              data={evaluations}
              columns={columns}
              getRowKey={(row) => row.id}
              emptyMessage="No transcript evaluations found"
              emptyDescription="Try adjusting your search or filter."
              onRowClick={(row) => openEvaluation(row.id)}
              isRowSelected={(row) => row.id === selectedId}
            />
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-[#e1e7ea] px-6 py-3">
              <span className="text-sm text-gray-500">
                Page {page} of {totalPages} · {total} total
              </span>
              <div className="flex gap-2">
                <Button
                  variant="neutral"
                  size="sm"
                  disabled={page <= 1 || isFetching}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="neutral"
                  size="sm"
                  disabled={page >= totalPages || isFetching}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </section>
      </div>

      <EvaluationReviewDrawer
        evaluationId={selectedId}
        placeholder={selectedPlaceholder}
        onClose={closeEvaluation}
      />

      <AdminAddTranscriptModal isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} />
    </div>
  );
};
